import { useAuth } from '@/lib/auth/auth';
import type { FollowStatusResponse, VenueDetail, VenueOwnerOverview } from '@/lib/api/apiTypes';
import { venueDetailKeys } from '@/features/venues/useVenueDetail';
import { venueOwnerOverviewKeys } from '@/features/venues/useVenueOwnerOverview';
import { useWritableMarketMutation } from '@/features/market/useWritableMarketMutation';
import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';

const venueFollowKeys = {
  all: ['venue-follow'] as const,
  detail: (venueID: string, username: string) => [...venueFollowKeys.all, venueID, username] as const,
};

type VenueFollowSnapshot = {
  followStatus: [QueryKey, FollowStatusResponse | undefined] | null;
  venueDetail: [QueryKey, VenueDetail | undefined] | null;
  venueOwnerOverview: [QueryKey, VenueOwnerOverview | undefined] | null;
};

export function useVenueFollow(
  venueID?: string | null,
  username?: string | null,
  enabled = true
) {
  const session = useAuth((state) => state.session);
  const request = useAuth((state) => state.request);
  const queryClient = useQueryClient();

  const followStatusQuery = useQuery({
    queryKey: venueID && username ? venueFollowKeys.detail(venueID, username) : venueFollowKeys.all,
    enabled: Boolean(session && username && venueID && enabled),
    queryFn: () => {
      if (!username || !venueID) {
        throw new Error('Missing venue follow target');
      }
      return request<FollowStatusResponse>(`/follow/${username}/status?venue_id=${venueID}`);
    },
    staleTime: 30_000,
  });

  const toggleFollow = useWritableMarketMutation(useMutation({
    mutationFn: async ({ isFollowing }: { isFollowing: boolean }) => {
      if (!username || !venueID) {
        throw new Error('Missing venue follow target');
      }

      const query = `venue_id=${encodeURIComponent(venueID)}`;
      if (isFollowing) {
        await request(`/profiles/${username}/follow?${query}`, { method: 'DELETE' });
        return false;
      }

      await request(`/profiles/${username}/follow?${query}`, { method: 'POST' });
      return true;
    },
    onMutate: async ({ isFollowing }) => {
      const snapshot: VenueFollowSnapshot = {
        followStatus: null,
        venueDetail: null,
        venueOwnerOverview: null,
      };

      if (username && venueID) {
        const followStatusKey = venueFollowKeys.detail(venueID, username);
        await queryClient.cancelQueries({ queryKey: followStatusKey });
        snapshot.followStatus = [followStatusKey, queryClient.getQueryData<FollowStatusResponse>(followStatusKey)];
        queryClient.setQueryData<FollowStatusResponse>(followStatusKey, { is_following: !isFollowing });
      }

      const delta = isFollowing ? -1 : 1;

      if (venueID) {
        const venueDetailKey = venueDetailKeys.detail(venueID);
        await queryClient.cancelQueries({ queryKey: venueDetailKey });
        snapshot.venueDetail = [venueDetailKey, queryClient.getQueryData<VenueDetail>(venueDetailKey)];
        queryClient.setQueryData<VenueDetail>(venueDetailKey, (current) =>
          current
            ? {
                ...current,
                follower_count: Math.max(0, current.follower_count + delta),
              }
            : current
        );

        const venueOwnerOverviewKey = venueOwnerOverviewKeys.detail(venueID);
        await queryClient.cancelQueries({ queryKey: venueOwnerOverviewKey });
        snapshot.venueOwnerOverview = [
          venueOwnerOverviewKey,
          queryClient.getQueryData<VenueOwnerOverview>(venueOwnerOverviewKey),
        ];
        queryClient.setQueryData<VenueOwnerOverview>(venueOwnerOverviewKey, (current) =>
          current
            ? {
                ...current,
                stats: {
                  ...current.stats,
                  followers: Math.max(0, current.stats.followers + delta),
                },
              }
            : current
        );
      }

      return snapshot;
    },
    onError: (_error, _variables, context) => {
      if (context?.followStatus) {
        queryClient.setQueryData(context.followStatus[0], context.followStatus[1]);
      }
      if (context?.venueDetail) {
        queryClient.setQueryData(context.venueDetail[0], context.venueDetail[1]);
      }
      if (context?.venueOwnerOverview) {
        queryClient.setQueryData(context.venueOwnerOverview[0], context.venueOwnerOverview[1]);
      }
    },
    onSettled: async () => {
      if (username && venueID) {
        await queryClient.invalidateQueries({ queryKey: venueFollowKeys.detail(venueID, username) });
      }
      if (venueID) {
        await queryClient.invalidateQueries({ queryKey: venueDetailKeys.detail(venueID) });
        await queryClient.invalidateQueries({ queryKey: venueOwnerOverviewKeys.detail(venueID) });
      }
    },
  }));

  return {
    isFollowing: followStatusQuery.data?.is_following ?? false,
    isLoading: followStatusQuery.isPending,
    toggleFollow,
  };
}
